/**
 * Cliente da API pública de envio de atestado pelo funcionário.
 * Sem login: NÃO usa getHeaders() (exige sessão). O token de sessão do envio vem da própria API.
 */
import { getApiUrl } from "./api-helpers";
import { medirEnvio, registrarFalhaEnvio, type OperacaoEnvio } from "./sentry-envio";

export const MAX_BYTES = 10 * 1024 * 1024;
const ERRO_REDE =
  "Não foi possível concluir agora. Tente de novo em instantes.";

export class EnvioApiError extends Error {
  status: number;
  /** Status 0: sem resposta (rede) ou resposta 2xx sem JSON (a API pode ter feito o trabalho). */
  causa?: "rede" | "resposta_invalida";
  constructor(status: number, message: string, causa?: "rede" | "resposta_invalida") {
    super(message);
    this.name = "EnvioApiError";
    this.status = status;
    this.causa = causa;
  }
}

export interface Canal {
  nome: string;
  logoUrl: string | null;
}

/** Canal do cliente, devolvido por identificar; o fluxo segue pelo slug dele. */
export interface CanalDoCliente extends Canal {
  slug: string;
}
/** Só o nome curto: RE e unidade não saem antes do envio. */
export interface FuncionarioResumo {
  nome: string;
}

/** Toda chamada é medida (span) e toda falha passa pelo registro do Sentry (ver sentry-envio.ts). */
function chamar<T>(
  operacao: OperacaoEnvio,
  caminho: string,
  init: { method?: string; body?: unknown; token?: string } = {},
): Promise<T> {
  return medirEnvio(operacao, async () => {
    try {
      return await requisitar<T>(caminho, init);
    } catch (e) {
      registrarFalhaEnvio(operacao, e);
      throw e;
    }
  });
}

async function requisitar<T>(
  caminho: string,
  init: { method?: string; body?: unknown; token?: string },
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (init.token) headers.Authorization = `Bearer ${init.token}`;
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}/envio-colaborador/${caminho}`, {
      method: init.method ?? (init.body === undefined ? "GET" : "POST"),
      headers,
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new EnvioApiError(0, ERRO_REDE, "rede");
  }
  let dados: Record<string, unknown> | null = null;
  try {
    const texto = await res.text();
    const json: unknown = texto ? JSON.parse(texto) : null;
    if (json && typeof json === "object")
      dados = json as Record<string, unknown>;
  } catch {
    /* corpo ilegível ou não-JSON */
  }
  if (!res.ok) {
    // 5xx: a mensagem do servidor é técnica; o funcionário vê só a genérica.
    const msg =
      res.status < 500 && typeof dados?.message === "string"
        ? dados.message
        : ERRO_REDE;
    throw new EnvioApiError(res.status, msg);
  }
  // 2xx sem corpo JSON (proxy, resposta cortada): não dá para seguir como se tivesse dado certo.
  if (!dados) throw new EnvioApiError(0, ERRO_REDE, "resposta_invalida");
  return dados as T;
}

const enc = (s: string) => encodeURIComponent(s);

export const obterCanal = (slug: string) => chamar<Canal>("canal", enc(slug));

export interface EmpresaDoGrupo {
  id: string;
  nome: string;
}

/**
 * CPF + data de nascimento (+ e-mail opcional). Pessoa em mais de uma empresa: `escolherEmpresa`
 * (sem token) — a tela pergunta e chama de novo com `empresa` e um token novo do Turnstile.
 */
export const identificar = (
  slug: string,
  dados: {
    cpf: string;
    nascimento: string;
    email?: string;
    turnstileToken: string;
    empresa?: string;
  },
) =>
  chamar<
    | { token: string; canal: CanalDoCliente; funcionario: FuncionarioResumo }
    | { escolherEmpresa: EmpresaDoGrupo[] }
  >("identificar", `${enc(slug)}/identificar`, { body: dados });

export const verificarArquivo = (
  slug: string,
  token: string,
  fileBase64: string,
) =>
  chamar<{ digitalValido: boolean; motivo: string }>(
    "verificar_arquivo",
    `${enc(slug)}/arquivo/verificar`,
    { body: { fileBase64 }, token },
  );

export const enviarAtestado = (
  slug: string,
  token: string,
  dados: {
    fileBase64: string;
    fileName: string;
    tipoEscolhido: "digital" | "fisico";
    termoAceito: true;
  },
) =>
  chamar<{ protocolo: string }>("enviar", `${enc(slug)}/atestado`, {
    body: dados,
    token,
  });

export function arquivoParaBase64(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:.*?;base64,/, ""));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(f);
  });
}
